import * as React from "react";
import Box from "@mui/material/Box";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext, useState } from "react";

import {
  Navigate,
  Outlet,
  matchRoutes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AppContext } from "./AppContextProvider";
import {
  Email,
  ExtensionOutlined,
  Logout,
  People,
  Settings,
  ViewList,
  Inventory2,
  CompareArrows,
  Draw,
  ForumOutlined,
} from "@mui/icons-material";
import { NULL_USER, Role } from "./models/User";
import { grey } from "@mui/material/colors";
import { downloadChromeExtensionZip } from "./api/ProcurementClient";
import { useWorkQueueAutoRefresh } from "./hooks/useWorkQueueAutoRefresh";
import { WorkQueuePurchaseOrdersProvider } from "./hooks/useWorkQueuePurchaseOrders";
import RcProvider from "./components/rc/RcProvider";
import GlobalRfqSearch from "./components/GlobalRfqSearch";
import ChatProvider, { useChat } from "./components/chat/ChatProvider";
import ChatSidebar from "./components/chat/ChatSidebar";
import { CHAT_URL } from "./components/chat/chatRoutes";

const ALT_COLOR = "#ffffff";

export const REGISTRATION_URL = "/register";
export const INVITE_URL = "/invite";
export const ACCOUNT_URL = "/account";
export const USERS_URL = "/users";
export const EMAILS_URL = "/emails";
export const EMAIL_DETAIL_URL = "/emails/:emailId";

/** Inbox list or open email; optional mailbox query preserves Team Rocket vs sales tab. */
export function buildEmailsUrl(emailId?: number, mailbox?: string): string {
  const params = new URLSearchParams();
  if (mailbox?.trim()) {
    params.set("mailbox", mailbox.trim().toLowerCase());
  }
  const qs = params.toString();
  const base =
    emailId != null && emailId > 0
      ? EMAIL_DETAIL_URL.replace(":emailId", String(emailId))
      : EMAILS_URL;
  return qs ? `${base}?${qs}` : base;
}
export const CUSTOMERS_URL = "/customers";
export const BUYERS_URL = "/buyers";
export const VENDORS_URL = "/vendors";
export const QC_CODES_URL = "/qc-codes";
export const PART_NUMBER_TYPES_URL = "/part-number-types";
export const FEE_TYPES_URL = "/fee-types";
export const NOTE_CATEGORIES_URL = "/note-categories";
export const RFQ_STATUSES_URL = "/rfq-statuses";
export const DELIVERY_METHODS_URL = "/delivery-methods";
export const DELIVERY_SPEEDS_URL = "/delivery-speeds";
export const TAX_CERTIFICATE_URL = "/tax-certificate";
export const ADMIN_FEES_URL = "/admin-fees";
export const RFQ_TIMING_URL = "/rfq-timing";
export const LOGIN_URL = "/login";
export const QUEUE_URL = "/queue";
export const ZENKIT_URL = "/zenkit";
export const QB_ITEMS_URL = "/qb-items";
export const ROCKET_COMPARE_URL = "/rocket-compare";
export const PROOF_OF_DELIVERY_URL = "/proof-of-delivery";
export const PROOF_OF_DELIVERY_NEW_URL = "/proof-of-delivery/new";
export const PROOF_OF_DELIVERY_CAPTURE_URL = "/proof-of-delivery/capture";
export const PROOF_OF_DELIVERY_DETAIL_URL = "/proof-of-delivery/:podId";

/** Navigate to Zenkit queue with one or more RFQs pre-selected. */
export const buildZenkitUrl = (rfqIds?: number[]): string => {
  if (!rfqIds?.length) {
    return ZENKIT_URL;
  }
  const unique = Array.from(new Set(rfqIds.filter((id) => id > 0)));
  if (!unique.length) {
    return ZENKIT_URL;
  }
  return `${ZENKIT_URL}?rfqIds=${unique.join(",")}`;
};
export const ORDERS_URL = "/orders";
export const VENDOR_ORDERS_URL = "/vendor-orders";
export const COMPLETED_POS_URL = "/completed-pos";
export const SAM_GOV_OPPORTUNITIES_URL = "/sam-gov-opportunities";
export const ALL_ITEMS_URL = "/all-items"; // legacy; redirects to Completed POs
export const PRICE_QUOTES_URL = "/price-quotes";
export const PRICE_QUOTE_REVIEW_URL = "/price-quotes/:priceQuoteId";
export const PO_REVIEW_URL = "/purchase-orders/:poId/review";
export const INVOICES_URL = "/invoices";
export const RFQ_URL = "/rfq/:rfqId";
export const RFQ_HISTORY_URL = "/rfq/:rfqId/history";
export const PO_URL = "/purchase-order/:poId";
export const PO_VENDORS_URL = "/purchase-order/:poId/vendors";
export const PO_ITEMS_URL = "/purchase-order/:poId/items";
export const INVOICE_URL = "/invoice/:invoiceId";
export const NOT_FOUND_URL = "/404";

/** React Router `location.state` when opening an RFQ — used so browser Back returns to queue or inbox. */
export type RfqLocationState = {
  rfqReturnPath?: string;
  /** Inbox/detail email row id — prefer over `Rfq.emailId` when archiving after approve (RFQ may not carry `emailId` in session). */
  sourceEmailId?: number;
};

export function getRfqReturnPath(state: unknown): string {
  const path = (state as RfqLocationState | null)?.rfqReturnPath;
  if (
    path === EMAILS_URL ||
    (typeof path === "string" && path.startsWith(`${EMAILS_URL}/`)) ||
    path === QUEUE_URL ||
    path === ORDERS_URL ||
    path === VENDOR_ORDERS_URL ||
    path === COMPLETED_POS_URL ||
    path === SAM_GOV_OPPORTUNITIES_URL ||
    path === PRICE_QUOTES_URL ||
    (typeof path === "string" && path.startsWith("/price-quotes/"))
  ) {
    return path;
  }
  return QUEUE_URL;
}

export const NOTIFICATIONS_URL = "/notifications";

export enum SidebarSelection {
  ACCOUNT = "Account",
  ORDERS = "Orders",
  RFQ = "RFQ",
  USERS = "Users",
  LOGOUT = "Logout",
}

const primaryColor = "primary.dark";

interface SidebarProps {
  onLogout: () => void;
}

const CHROME_EXTENSION_INSTALL_STEPS = [
  "Right click the downloaded file → Extract All",
  "Go to chrome://extensions",
  "Ensure Developer mode in the top right is enabled",
  "Remove the existing Chrome extension (if installed)",
  'Select "Load unpacked"',
  "Add the extracted folder",
  "Refresh the program",
];

const DownloadExtensionButton: React.FC = () => {
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const handleDownload = async () => {
    setInstructionsOpen(true);
    const blob = await downloadChromeExtensionZip();
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = `procurement-chrome-extension-${new Date()
      .toISOString()
      .slice(0, 10)}.zip`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  return (
    <>
      <Tooltip title="Download Chrome Extension zip">
        <ExtensionOutlined
          onClick={handleDownload}
          sx={{ cursor: "pointer", p: 1, color: ALT_COLOR }}
          fontSize="large"
        />
      </Tooltip>
      <Dialog
        open={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Install Chrome Extension</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Your download should start automatically. Then follow these steps:
          </Typography>
          <List dense disablePadding>
            {CHROME_EXTENSION_INSTALL_STEPS.map((step, index) => (
              <ListItem key={step} disableGutters sx={{ alignItems: "flex-start" }}>
                <ListItemText
                  primary={`${index + 1}. ${step}`}
                  primaryTypographyProps={{ variant: "body1" }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstructionsOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const UsersButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = matchRoutes(
    [
      { path: USERS_URL },
      { path: CUSTOMERS_URL },
      { path: BUYERS_URL },
      { path: VENDORS_URL },
      { path: QC_CODES_URL },
      { path: PART_NUMBER_TYPES_URL },
      { path: FEE_TYPES_URL },
      { path: NOTE_CATEGORIES_URL },
      { path: RFQ_STATUSES_URL },
      { path: DELIVERY_METHODS_URL },
      { path: DELIVERY_SPEEDS_URL },
      { path: TAX_CERTIFICATE_URL },
      { path: ADMIN_FEES_URL },
      { path: RFQ_TIMING_URL },
    ],
    location.pathname
  );

  return (
    <People
      onClick={() => {
        if (!isSelected) {
          navigate(USERS_URL);
        }
      }}
      sx={{
        cursor: !isSelected ? "pointer" : undefined,
        p: 1,
        backgroundColor: isSelected ? ALT_COLOR : undefined,
        color: isSelected ? primaryColor : ALT_COLOR,
      }}
      fontSize="large"
    />
  );
};

const EmailsButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = Boolean(
    matchRoutes(
      [{ path: EMAILS_URL }, { path: EMAIL_DETAIL_URL }],
      location.pathname
    )
  );

  return (
    <Email
      onClick={() => {
        if (!isSelected) {
          navigate(EMAILS_URL);
        }
      }}
      sx={{
        cursor: !isSelected ? "pointer" : undefined,
        p: 1,
        backgroundColor: isSelected ? ALT_COLOR : undefined,
        color: isSelected ? primaryColor : ALT_COLOR,
      }}
      fontSize="large"
    />
  );
};

const SettingsButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = matchRoutes([{ path: ACCOUNT_URL }], location.pathname);

  return (
    <Settings
      onClick={() => {
        if (!isSelected) {
          navigate(ACCOUNT_URL);
        }
      }}
      sx={{
        cursor: !isSelected ? "pointer" : undefined,
        p: 1,
        backgroundColor: isSelected ? ALT_COLOR : undefined,
        color: isSelected ? primaryColor : ALT_COLOR,
      }}
      fontSize="large"
    />
  );
};

const QueuesButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Review pages are not the queue itself — keep the icon clickable so users can
  // return to /queue the same way they can from an open RFQ.
  const isSelected = matchRoutes(
    [
      { path: QUEUE_URL },
      { path: ORDERS_URL },
      { path: VENDOR_ORDERS_URL },
      { path: COMPLETED_POS_URL },
      { path: SAM_GOV_OPPORTUNITIES_URL },
      { path: ALL_ITEMS_URL },
      { path: PRICE_QUOTES_URL },
    ],
    location.pathname
  );
  return (
    <ViewList
      onClick={() => {
        if (!isSelected) {
          navigate(QUEUE_URL);
        }
      }}
      sx={{
        cursor: !isSelected ? "pointer" : undefined,
        p: 1,
        backgroundColor: isSelected ? ALT_COLOR : undefined,
        color: isSelected ? primaryColor : ALT_COLOR,
      }}
      fontSize="large"
    />
  );
};

const ZenkitButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = matchRoutes([{ path: `${ZENKIT_URL}/*` }], location.pathname);
  return (
    <Tooltip title="Zenkit">
      <SvgIcon
        onClick={() => {
          if (!isSelected) {
            navigate(ZENKIT_URL);
          }
        }}
        sx={{
          cursor: !isSelected ? "pointer" : undefined,
          p: 1,
          backgroundColor: isSelected ? ALT_COLOR : undefined,
          color: isSelected ? primaryColor : ALT_COLOR,
        }}
        fontSize="large"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <rect
          x="3.75"
          y="3.75"
          width="16.5"
          height="16.5"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <text
          x="12"
          y="17"
          textAnchor="middle"
          fill="currentColor"
          fontFamily='system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
          fontSize="13"
          fontWeight="700"
        >
          Z
        </text>
      </SvgIcon>
    </Tooltip>
  );
};

const QbItemsButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = matchRoutes([{ path: QB_ITEMS_URL }], location.pathname);
  return (
    <Tooltip title="Items">
      <Inventory2
        onClick={() => {
          if (!isSelected) {
            navigate(QB_ITEMS_URL);
          }
        }}
        sx={{
          cursor: !isSelected ? "pointer" : undefined,
          p: 1,
          backgroundColor: isSelected ? ALT_COLOR : undefined,
          color: isSelected ? primaryColor : ALT_COLOR,
        }}
        fontSize="large"
      />
    </Tooltip>
  );
};

const RocketCompareButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = matchRoutes(
    [{ path: ROCKET_COMPARE_URL }],
    location.pathname,
  );
  return (
    <Tooltip title="Rocket Compare">
      <CompareArrows
        onClick={() => {
          if (!isSelected) {
            navigate(ROCKET_COMPARE_URL);
          }
        }}
        sx={{
          cursor: !isSelected ? "pointer" : undefined,
          p: 1,
          backgroundColor: isSelected ? ALT_COLOR : undefined,
          color: isSelected ? primaryColor : ALT_COLOR,
        }}
        fontSize="large"
      />
    </Tooltip>
  );
};

const ProofOfDeliveryButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = matchRoutes(
    [
      { path: PROOF_OF_DELIVERY_URL },
      { path: PROOF_OF_DELIVERY_NEW_URL },
      { path: PROOF_OF_DELIVERY_CAPTURE_URL },
      { path: PROOF_OF_DELIVERY_DETAIL_URL },
    ],
    location.pathname,
  );
  return (
    <Tooltip title="Proof of Delivery">
      <Draw
        onClick={() => {
          if (!isSelected) {
            navigate(PROOF_OF_DELIVERY_URL);
          }
        }}
        sx={{
          cursor: !isSelected ? "pointer" : undefined,
          p: 1,
          backgroundColor: isSelected ? ALT_COLOR : undefined,
          color: isSelected ? primaryColor : ALT_COLOR,
        }}
        fontSize="large"
      />
    </Tooltip>
  );
};

const ChatButton: React.FC = () => {
  const location = useLocation();
  const { totalUnread, totalMentions, sidebar, updateSidebar } = useChat();
  const onChatPage = Boolean(matchRoutes([{ path: `${CHAT_URL}/*` }], location.pathname));
  const isSelected = onChatPage || sidebar.open;
  return (
    <Tooltip
      title={onChatPage ? "Chat" : sidebar.open ? "Hide chat" : "Show chat"}
      placement="right"
    >
      <Box
        role="button"
        aria-label={sidebar.open ? "Hide chat" : "Show chat"}
        aria-pressed={isSelected}
        onClick={() => {
          if (!onChatPage) {
            updateSidebar({ open: !sidebar.open });
          }
        }}
        sx={{
          cursor: !onChatPage ? "pointer" : undefined,
          backgroundColor: isSelected ? ALT_COLOR : undefined,
          color: isSelected ? primaryColor : ALT_COLOR,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Badge
          badgeContent={totalMentions > 0 ? "@" : totalUnread}
          color="error"
          max={99}
          overlap="circular"
          invisible={totalUnread === 0 && totalMentions === 0}
          sx={{ "& .MuiBadge-badge": { top: 10, right: 10 } }}
        >
          <ForumOutlined sx={{ p: 1 }} fontSize="large" />
        </Badge>
      </Box>
    </Tooltip>
  );
};

const AdminSidebar: React.FC<SidebarProps> = ({ onLogout }) => {
  return (
    <Box
      className="sidebar"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pt: 1,
        pb: 1,
        backgroundColor: primaryColor,
        borderStyle: "solid",
        borderRightWidth: 1,
        borderColor: primaryColor,
      }}
    >
      <EmailsButton />
      <QueuesButton />
      <ZenkitButton />
      <RocketCompareButton />
      <ProofOfDeliveryButton />
      <QbItemsButton />
      <UsersButton />
      <ChatButton />
      <Divider sx={{ borderColor: "white", width: "100%" }} />
      <SettingsButton />
      <Logout
        onClick={onLogout}
        sx={{ cursor: "pointer", p: 1, color: ALT_COLOR }}
        fontSize="large"
      />
      <Box sx={{ flexGrow: 1 }} />
      <DownloadExtensionButton />
    </Box>
  );
};

const CustomerSidebar: React.FC<SidebarProps> = ({ onLogout }) => {
  return (
    <Box
      className="sidebar"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pt: 1,
        pb: 1,
        backgroundColor: primaryColor,
        borderStyle: "solid",
        borderRightWidth: 1,
        borderColor: primaryColor,
      }}
    >
      <QueuesButton />
      <ZenkitButton />
      <RocketCompareButton />
      <QbItemsButton />
      <Divider sx={{ borderColor: "white", width: "100%" }} />
      <SettingsButton />
      <Logout
        onClick={onLogout}
        sx={{ cursor: "pointer", p: 1, color: ALT_COLOR }}
        fontSize="large"
      />
      <Box sx={{ flexGrow: 1 }} />
      <DownloadExtensionButton />
    </Box>
  );
};

const EmployeeSidebar: React.FC<SidebarProps> = ({ onLogout }) => {
  return (
    <Box
      className="sidebar"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pt: 1,
        pb: 1,
        backgroundColor: primaryColor,
        borderStyle: "solid",
        borderRightWidth: 1,
        borderColor: primaryColor,
      }}
    >
      <EmailsButton />
      <QueuesButton />
      <ZenkitButton />
      <RocketCompareButton />
      <ProofOfDeliveryButton />
      <QbItemsButton />
      <ChatButton />
      <Divider sx={{ borderColor: "white", width: "100%" }} />
      <SettingsButton />
      <Logout
        onClick={onLogout}
        sx={{ cursor: "pointer", p: 1, color: ALT_COLOR }}
        fontSize="large"
      />
      <Box sx={{ flexGrow: 1 }} />
      <DownloadExtensionButton />
    </Box>
  );
};

const Sidebar: React.FC = () => {
  const { user, setUser, setUsers, setCustomers } = useContext(AppContext);

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    setUser(NULL_USER);
    setUsers([]);
    setCustomers([]);
    navigate(LOGIN_URL);
  };

  let sidebar = <CustomerSidebar onLogout={handleLogout} />;
  switch (user.role) {
    case Role.ADMIN:
      sidebar = <AdminSidebar onLogout={handleLogout} />;
      break;
    case Role.EMPLOYEE:
      sidebar = <EmployeeSidebar onLogout={handleLogout} />;
      break;
  }

  return <>{sidebar}</>;
};

const WorkQueueAutoRefresh: React.FC = () => {
  const location = useLocation();
  const pollList = Boolean(
    matchRoutes(
      [
        { path: QUEUE_URL },
        { path: ORDERS_URL },
        { path: VENDOR_ORDERS_URL },
        { path: COMPLETED_POS_URL },
        { path: ALL_ITEMS_URL },
        { path: PRICE_QUOTES_URL },
      ],
      location.pathname
    )
  );
  // Socket sync runs everywhere; backup poll only on list pages.
  useWorkQueueAutoRefresh(pollList);
  return null;
};

const MainAppPanel: React.FC = () => {
  const location = useLocation();
  const { user } = useContext(AppContext);
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const onPodRoute = Boolean(
    matchRoutes(
      [
        { path: PROOF_OF_DELIVERY_URL },
        { path: PROOF_OF_DELIVERY_NEW_URL },
        { path: PROOF_OF_DELIVERY_CAPTURE_URL },
        { path: PROOF_OF_DELIVERY_DETAIL_URL },
      ],
      location.pathname
    )
  );
  // Full-screen POD on phones; keep rail on desktop list for office nav.
  const hideSidebar =
    onPodRoute &&
    (isNarrow ||
      Boolean(
        matchRoutes(
          [
            { path: PROOF_OF_DELIVERY_NEW_URL },
            { path: PROOF_OF_DELIVERY_CAPTURE_URL },
            { path: PROOF_OF_DELIVERY_DETAIL_URL },
          ],
          location.pathname
        )
      ));

  const isStaff = user.role === Role.ADMIN || user.role === Role.EMPLOYEE;
  const showGlobalSearch = !hideSidebar && isStaff;

  if (matchRoutes([{ path: "/" }, { path: "/admin" }], location.pathname)) {
    return <Navigate to={QUEUE_URL} />;
  }
  return (
    <WorkQueuePurchaseOrdersProvider>
      <WorkQueueAutoRefresh />
      <ChatProvider enabled={isStaff}>
        <RcProvider>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
            }}
          >
            {!hideSidebar && <Sidebar />}
            <Box
              className="wrapperpanel"
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                backgroundColor: grey[100],
              }}
            >
              {showGlobalSearch && (
                <Box
                  sx={{
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    px: 2,
                    py: 0.75,
                    borderBottom: `1px solid ${grey[300]}`,
                    backgroundColor: grey[50],
                  }}
                >
                  <GlobalRfqSearch />
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Outlet />
              </Box>
            </Box>
            <ChatSidebar suppressed={hideSidebar} />
          </Box>
        </RcProvider>
      </ChatProvider>
    </WorkQueuePurchaseOrdersProvider>
  );
};

export default MainAppPanel;
